'use client';

import { useState } from 'react';
import type { FormEvent } from 'react';

const CustomCheckbox = ({ isChecked, onChange }: { isChecked: boolean, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) => (
  <div className="flex items-center">
    <input
      id="consent"
      name="consent"
      type="checkbox"
      checked={isChecked}
      onChange={onChange}
      className="
                h-5 w-5 rounded border-2 border-gray-500 cursor-pointer transition-all duration-200 ease-in-out
                checked:bg-[#FF7C22] checked:border-[#FF7C22] checked:hover:bg-[#FFA05E]
                focus:ring-2 focus:ring-offset-2 focus:ring-[#627EFB] focus:ring-offset-[#001D3D] 
                appearance-none shrink-0 // Добавлен shrink-0, чтобы не сжимался
            "
      style={{
        backgroundImage: isChecked
          ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E")`
          : 'none',
        backgroundSize: '80%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}
    />
    <label htmlFor="consent" className="ml-3 text-sm text-gray-300 cursor-pointer">
      Я соглашаюсь с <a href="#" className="text-[#FF7C22] hover:underline">обработкой персональных данных</a>
    </label>
  </div>
);


const ApplicationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    message: '',
    consent: false,
  });

  const [formStatus, setFormStatus] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox'
      ? (e.target as HTMLInputElement).checked
      : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.consent) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    setFormStatus('Отправка...');

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/application-requests`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            formName: 'Оставить заявку', 
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            position: formData.position,
            message: formData.message,
            consent: formData.consent,
          },
        }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена!');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          position: '',
          message: '',
          consent: false,
        });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Произошла неизвестная ошибка при отправке.';
        setFormStatus(`Произошла ошибка: ${errorMessage}`);
        console.error('Ошибка:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, попробуйте снова.');
      console.error('Ошибка:', error);
    }
  };

  const inputClasses = "w-full p-4 rounded-lg bg-[#002855] text-white border border-[#00386C] placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#627EFB]";

  return (
    <div className="bg-[#001D3D] text-white font-sans">
      <div className="container mx-auto px-4 pt-36 pb-16 min-h-screen">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Оставить заявку на подбор
          </h1>
          <p className="text-center text-gray-300 mb-12 text-lg">
            Заполните форму, и наш менеджер свяжется с вами, чтобы обсудить ваши потребности в подборе персонала.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-gray-300 mb-2">Имя</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Ваше имя"
                required
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-gray-300 mb-2">Название компании</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="ООО “Интерим”"
                required
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="position" className="block text-gray-300 mb-2">Должность, на которую ищете сотрудника</label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="Например, Менеджер по продажам"
                required
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-gray-300 mb-2">Номер телефона</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+7 (XXX) XXX-XX-XX"
                required
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-gray-300 mb-2">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-gray-300 mb-2">Требования к кандидату</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                placeholder="Опишите необходимые навыки, опыт, график работы и другие детали"
                className={`${inputClasses} resize-none`}
              />
            </div>

            <div>
              <CustomCheckbox
                isChecked={formData.consent}
                onChange={handleChange}
              />
            </div>

            <div>
              <button
                type="submit"
                className="w-full py-4 rounded-xl text-white font-semibold text-lg bg-[#FF7C22] hover:bg-[#FFA05E] transition-all duration-300"
              >
                Отправить заявку
              </button>
              {formStatus && (
                <p className={`mt-4 text-sm text-center ${formStatus.startsWith('❌') ? 'text-red-400' : 'text-green-400'}`}>
                  {formStatus}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationPage;
